<template>
  <ElConfigProvider :locale="ElRuLocale">
    <div class="ps-app theme-light">
      <AppUpdater />
      <div
        v-if="initStore.status === TabInitStatus.workerBroken"
        class="no-worker-notification"
      >
        <!-- TODO: show as notification -->
        Приложение сломалось :(
        <br>Закройте все вкладки ИС и откройте заново
      </div>
      <SplashScreen v-if="initStore.isLoading" />
      <template v-else>
        <Auth v-if="initStore.isAuthRequired" />
        <template v-else>
          <ConnectionStatus />
          <MainGrid @logout="onLogout">
            <template #default="{ isTaskPanelShown }">
              <RouterView v-slot="{ Component }">
                <template v-if="Component">
                  <Suspense>
                    <component
                      :is="Component"
                      :isTaskPanelShowed="isTaskPanelShown"
                    />
                  </Suspense>
                </template>
              </RouterView>
            </template>
          </MainGrid>
          <ShareModal
            v-if="shareModalVisible"
            :modelValue="shareModalVisible"
            @update:modelValue="toggleShareModal"
          />
        </template>
      </template>

      <div id="tooltips" />
      <div id="popups" />
      <div id="popovers" />
      <div id="message" />
    </div>
  </ElConfigProvider>
</template>

<script setup lang="ts">
import { onMounted, provide } from 'vue'
import { RouterView } from 'vue-router'
import { ElConfigProvider, ElNotification } from 'element-plus'
import ElRuLocale from 'element-plus/es/locale/lang/ru'
import AppUpdater from '@/ContextTab/components/AppUpdater.vue'
import ConnectionStatus from '@/ContextTab/components/ConnectionStatus.vue'
import SplashScreen from '@/ContextTab/components/SplashScreen.vue'
import MainGrid from '@/ContextTab/layout/grid/MainGrid.vue'
import Auth from '@/ContextTab/modules/auth/components/Auth.vue'
import ShareModal from '@/ContextTab/modules/news/share-modal/components/ShareModal.vue'
import { useShareModal } from '@/ContextTab/modules/news/composables/useShareModal'
import { KeyboardHandlerKey, NotificationHandlerKey } from '@/injectionKeys'
import { KeyboardHandler } from '@/ContextTab/services/keyboardHandler'
import { useTabDescription } from './composables/useTabDescription'
import { TabInitStatus, useInitStore } from '@/ContextTab/stores/init'
import { useAuthStore } from '@/ContextTab/stores/auth/auth'
import { useAuthWatcher } from '@/ContextTab/hooks/authWatcher'
import { useSessionWatcher } from '@/ContextTab/hooks/sessionWatcher'
import { useGatewayWatcher } from '@/ContextTab/hooks/gatewayWatcher'
import { useLegacyBridge } from '@/ContextTab/hooks/legacyWatcher'


const initStore = useInitStore()
const authStore = useAuthStore()

const { shareModalVisible, hideShareModal } = useShareModal()
const toggleShareModal = (value: boolean) => {
  if (!value) {
    hideShareModal(window.contextId)
  }
}

useAuthWatcher()
useSessionWatcher()
const { tryConnectWorker } = useGatewayWatcher()
useLegacyBridge()
useTabDescription()

provide(KeyboardHandlerKey, new KeyboardHandler())
provide(NotificationHandlerKey, { onError })

onMounted(async () => {
  initStore.setState(TabInitStatus.waitingWorker)
  const workerLoaded = await tryConnectWorker()
  if (!workerLoaded) {
    initStore.setState(TabInitStatus.workerBroken)
  } else {
    initStore.setState(TabInitStatus.waitingAuthCheck)
    await authStore.loadAndValidateTokens()
    // -> continue initialization in hooks/authWatchers :: watch authStore.status
  }
})

function onLogout() {
  authStore.logout()
}

function onError(e: unknown) {
  if (e instanceof Error) {
    ElNotification.error({
      title: e.message,
    })
  } else {
    console.error('Unknown error:', e)
  }
}
</script>

<style scoped>
  .ps-app {
    --workspace-switcher-size: 4.125rem;
    --task-panel-width: 18rem;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    position: relative;
    background: linear-gradient(90deg, #bde5e9, #fff, #bde5e9);
  }

  .no-worker-notification {
    text-align: center;
    margin-top: 5%;
    color: red;
  }
</style>
